import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { mixpanelTrack } from '@providers/Mixpanel';
import { useRouter } from 'next/router';

import * as graphQLModels from '@typings/graphql-models';
import { NoodleProductTypes, SubscriptionStatus } from '@typings/graphql-models';
import { getUrl, IDENTIFIERS } from 'helpers/urlsHelper';
import scrollParentToTop from 'helpers/scrollParentToTop';
import { isInIframe, isInPopoverWidget, isSubscriptionProduct } from '@helpers/helper';
import { useIsMobile, useToast, useUser } from 'hooks';
import BackButton from 'components/BackButton';
import formatParticipants from '@helpers/formatParticipants';
import BroadcastContainer from '@components/BroadcastContainer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { useFeatureFlag } from '@providers/FeatureFlags';
import { getCreatorProductComments, getCreatorProducts, getPurchasedProducts, retrySubscriptionPayment } from '@tsClient';

import { differenceInDays } from 'date-fns';

import RadioInputCards from '@components/FormFields/RadioInputCards';
import CaretCircleDown from '@components/Icons/CaretCircleDown';
import FunnelSimple from '@components/Icons/FunnelSimple';

import CustomLink from '@components/CustomLink';
import UserImage from '@components/UserImage';
import Image from '@components/Image';
import YourBookmarks from '@components/YourBookmarks';
import MarkCompleted from '@components/MarkCompleted';
import getMyUpcomingBookings from '@tsClient/my/getMyUpcomingBookings';
import BookingCard from '@components/BookingCard';
import ProgressIndicator from '@components/ProgressIndicator';
import UnscheduledBookings from '@components/UnscheduledBookings';
import removeNullish from '@helpers/removeNullish';
import UserSubscriptions from '@lib/UserSubscriptions';
import NewConversationWithCreator from '@lib/NewConversationWithCreator';
import { ToastTypeVariants } from '@context/ToastContext';
import CreateEditBroadcastModal from '@modals/CreateEditBroadcastModal';
import Chips from '@components/Chips';
import StartPostButton from '@components/StartPostButton';
import FilterChip from '@components/FilterChip';
import UserDocumentRequests from '@components/UserDocumentRequests';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import getUserWorkflows from '@tsClient/creators/getUserWorkflows';
import UserWorkflows from '@lib/UserWorkflows';
import UserWorkflowDetailCustomerView from '@lib/UserWorkflowDetailCustomerView';
import Spacer from '@components/Spacer';
import DOMPurify from 'isomorphic-dompurify';
import AssociatedUserSelect from '@components/AssociatedUserSelect';
import Pulse from '@components/Icons/Pulse';
import { m } from 'framer-motion';
import Buttons from '@components/Buttons';
import { getProfile } from '@tsClient/my';
import CreateOrganizationModal from '@modals/CreateOrganizationModal';
import * as format from '@format';
import ChatsCircle from '@components/Icons/ChatsCircle';
import { SIZES } from '@styles/media';
import NoodleIsNowGladeBanner from '@/components/NoodleIsNowGladeBanner';

import UserProfileShimmer from './UserProfileShimmer';
import s from './UserProfile.module.scss';
import CreatorActivity from './components/CreatorActivity';
import ALL_TABS from './constants';
import ManageTeamModal from './components/ManageTeamModal';

const UserProfile: React.FC<{ creatorSlug: string }> = ({ creatorSlug }) => {
  const router = useRouter();
  // Make sure any changes to query handling is also handled in the V3 UserProfileLayout
  const queryTab = deserializeQueryStringItem(router.query.tab);
  const userWorkflowId = deserializeQueryStringItem(router.query.userWorkflowId);
  const [currentTab, setCurrentTab] = useState<string | null>(queryTab || null);
  const [clickedByte, setClickedByte] = useState<string | null>(null);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [isCreateCommunityPostModalOpen, setCreateCommunityPostModalOpen] = useState(false);
  const [selectedChip, setSelectedChip] = useState<string | null>('View All');
  const [broadcastsOnly, setBroadcastsOnly] = useState(false);
  const [conversationSid, setConversationSid] = useState<string | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [isCreateOrganizationModalOpen, setCreateOrganizationModalOpen] = useState(false);

  const handleTabChange = (id: string): void => {
    const routerBasePath = router.asPath.split('?');
    const queryParamsStr = new URLSearchParams({ tab: id || '', userWorkflowId: userWorkflowId || '' }).toString();
    const queryParamsStrWithoutWorkflowId = new URLSearchParams({ tab: id || '' }).toString();
    const urlWithNewTab = currentTab === 'activity'
      ? `${routerBasePath[0]}?${queryParamsStrWithoutWorkflowId}`
      : `${routerBasePath[0]}?${queryParamsStr}`;
    router.push(urlWithNewTab, undefined, { shallow: true });
    setCurrentTab(id);
  };

  const [user] = useUser();
  const userId = user?.id;

  const { data: creatorProducts, fetchingState: fetchingCreatorProducts, getData: getCreatorInfo } = useNoodleApi(getCreatorProducts);
  const { data: comments, getData: getCommentsFn } = useNoodleApi(getCreatorProductComments);
  const { data: userProfile, getData: getUserProfileFn } = useNoodleApi(getProfile);
  const { data: purchasedProducts, fetchingState: fetchingPurchasedProducts, getData: getProducts } = useNoodleApi(getPurchasedProducts);
  const { data: upcomingBookings, fetchingState: fetchingBookings, getData: getUpcomingBookings } = useNoodleApi(getMyUpcomingBookings);
  const {
    data: subscriptionsData,
    fetchingState: fetchingSubscription,
    getData: getUserSubscriptionsFn,
  } = useNoodleApi(tsClient.getUserSubscriptions);
  const {
    data: documentRequests,
    fetchingState: fetchingDocumentRequests,
    getData: getDocumentRequestsFn,
  } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUsers);
  const { data: userWorkflows, fetchingState: fetchingUserWorkflows, getData: getUserWorkflowsFn } = useNoodleApi(getUserWorkflows);
  const { getData: retrySubscriptionFn } = useNoodleApi(retrySubscriptionPayment);
  const { data: creatorData, fetchingState: fetchingCreator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator);
  const { getData: findOrCreateConversationWithCreator } = useNoodleApi(tsClient.my.findOrCreateConversationWithCreator);
  const {
    data: organization,
    getData: getOrganization,
    fetchingState: { isFetching: isFetchingOrganization },
  } = useNoodleApi(tsClient.organizations.getOrganization);
  const { data: customer, getData: getNoodleCustomer } = useNoodleApi(tsClient.getNoodleCustomer);

  const addToast = useToast();

  const creatorName = creatorProducts?.artist?.[0]?.name;
  const creatorPersonId = creatorProducts?.artist?.[0]?.person?.id;

  const purchasedHandbooks = purchasedProducts?.filter(({ productTypes }) =>
    productTypes.some(p => p.noodleProductType === NoodleProductTypes.Handbook),
  );

  const showChatInProfile = useFeatureFlag('user-profile-chat');

  const scheduledBookings = upcomingBookings?.filter(b => b.startAt && b.endAt);
  const unscheduledBookingsByPrice = Object.values(
    upcomingBookings
      ?.filter(b => !b.startAt)
      ?.reduce(
        (acc: Record<string, Awaited<ReturnType<typeof getUpcomingBookings>>['data']>, val) => ({
          ...acc,
          // soon, price id will be required and not optional
          [val.price?.id || '']: [...(acc[val.price?.id || ''] || []), val],
        }),
        {},
      ) || {},
  ).filter(removeNullish);

  const handlePublishPost = async (): Promise<void> => {
    setCreateCommunityPostModalOpen(false);
    getCreatorInfo({ creatorSlug });
  };

  useEffect(() => {
    if (userId) {
      getProducts({ creatorSlug });
      getCreatorInfo({ creatorSlug });
      getUserProfileFn();
      getCommentsFn({ creatorSlug });
      getUpcomingBookings({ creatorSlug });
      mixpanelTrack('Viewed User Profile', {
        creator: creatorSlug,
      });
      scrollParentToTop();
      getUserSubscriptionsFn();
      getCreatorFn({ creatorSlug });
    }
  }, [userId, creatorSlug, getProducts, getCreatorInfo, getUserProfileFn, getCommentsFn, getUpcomingBookings, getCreatorFn, getUserSubscriptionsFn]);

  useEffect(() => {
    if (userId && creatorProducts) {
      getDocumentRequestsFn({ creatorId: creatorProducts.artist[0].id, page: 1, perPage: 100 });
    }
  }, [userId, creatorProducts, getDocumentRequestsFn]);

  useEffect(() => {
    if (userId && creatorProducts) {
      getUserWorkflowsFn({
        creatorId: creatorProducts.artist[0].id,
        page: 1,
        perPage: 1,
        personId: userId,
        status: [],
      });
    }
  }, [creatorProducts, getUserWorkflowsFn, userId]);

  const retryPayment = async (subscriptionId: string): Promise<void> => {
    const { error } = await retrySubscriptionFn({ subscriptionId });
    if (!error) {
      await getUserSubscriptionsFn();
    } else {
      addToast(ToastTypeVariants.ERROR, 'Payment failed. Please try a different payment method or try again.');
    }
  };

  const isMobile = useIsMobile(SIZES.xl);

  const isFetching = fetchingPurchasedProducts.isFetching
    || fetchingBookings.isFetching
    || fetchingDocumentRequests.isFetching
    || fetchingUserWorkflows.isFetching
    || fetchingCreatorProducts.isFetching;
  const broadcast = creatorProducts?.products.find(product => product.includesBroadcasts);
  const broadcastComments = broadcast?.comments?.filter(comment => comment.parent === null);
  const broadcastTags = Array.from(new Set(broadcastComments?.map(b => b?.tags?.map(tag => tag.name)).flat()));
  const filteredBroadcastsData = broadcastComments?.filter(b => b.tags?.map(tag => tag.name).includes(selectedChip as string));
  const broadcastData = selectedChip === 'View All' ? broadcastComments : filteredBroadcastsData;
  const hasDocumentRequests = (documentRequests?.numItems || 0) > 0;
  const hasUserWorkflows = (userWorkflows?.numItems || 0) > 0;
  const hasSubscriptionProduct = Boolean(creatorProducts?.products?.find(isSubscriptionProduct));
  const creatorTimezone = creatorProducts?.artist[0]?.timezone;
  const hasBookings = Boolean(upcomingBookings && (upcomingBookings?.length || 0) > 0 && creatorTimezone);
  const hasLoadedData = !isFetching && fetchingCreator.isFetched;
  const hasHandbooks = creatorProducts?.productTypes?.[0].productTypes
    && creatorProducts?.productTypes?.[0]
      .productTypes?.filter(productType => productType.noodleProductType === NoodleProductTypes.Handbook).length > 0;

  const tabsToShow = useMemo(
    () =>
      ALL_TABS.filter(tab => {
        if (!hasLoadedData) {
          return false;
        }

        if (tab.id === currentTab) {
          return true;
        }

        if (tab.id === 'bookings') {
          return hasBookings;
        }

        if (tab.id === 'community') {
          return hasSubscriptionProduct;
        }

        if (tab.id === 'bookmarks') {
          return purchasedHandbooks && purchasedHandbooks.length > 0;
        }

        if (tab.id === 'services') {
          return hasDocumentRequests && !hasUserWorkflows;
        }

        if (tab.id === 'activity') {
          return hasUserWorkflows;
        }

        if (tab.id === 'library') {
          return hasHandbooks;
        }

        return true;
      }).map((tab, idx) => ({
        ...tab,
        defaultChecked: currentTab ? currentTab === tab.id : idx === 1,
      })),
    [currentTab, hasLoadedData, hasBookings, hasSubscriptionProduct, hasHandbooks, purchasedHandbooks, hasDocumentRequests, hasUserWorkflows],
  );

  const showSidebar = !isMobile && !isInIframe();

  const hideMobileNavigation = isMobile && currentTab === 'activity' && userWorkflowId;

  useEffect(() => {
    if (hasLoadedData) {
      const hasTabs = tabsToShow.filter(tab => tab.id !== 'chat').length > 0;

      if (hasTabs && !queryTab) {
        handleTabChange(tabsToShow.filter(tab => tab.id !== 'chat')[0].id);
      }
    }
  }, [tabsToShow, router, creatorSlug, queryTab, hasLoadedData]);

  useEffect(() => {
    if (userId && creatorData?.id) {
      findOrCreateConversationWithCreator({
        creatorSlug,
      }).then(d => {
        setConversationSid(d.data?.sid || null);
      });
    }
  }, [userId, creatorData?.id, creatorSlug, findOrCreateConversationWithCreator, setConversationSid]);

  useEffect(() => {
    if (userId && creatorData?.id) {
      getOrganization({ creatorId: creatorData?.id });
    }
  }, [userId, creatorData?.id, getOrganization]);

  useEffect(() => {
    if (userId && creatorData?.id) {
      getNoodleCustomer({ creatorId: creatorData.id });
    }
  }, [userId, creatorData?.id, getNoodleCustomer]);

  if (isFetching === undefined || isFetching) {
    return <UserProfileShimmer />;
  }

  let subscriptions: NonNullable<typeof subscriptionsData> = [];

  if (!fetchingSubscription.isFetching && subscriptionsData?.length) {
    subscriptions = (subscriptionsData || [])
      .filter(sub => sub?.subscriptionModel?.product?.creator?.slug === creatorSlug)
      .filter(
        item =>
          item.subscriptionStatus !== graphQLModels.SubscriptionStatus.Incomplete
          && item.subscriptionStatus !== graphQLModels.SubscriptionStatus.IncompleteExpired,
      );
  }

  const getLibrary = (): ReactElement | null => {
    const allProductTypes = creatorProducts?.productTypes[0]?.productTypes.filter(
      productType => productType.noodleProductType === NoodleProductTypes.Handbook,
    );

    const allCourses = creatorData?.courses;

    const listProductTypes = (courseId?: string): ReactElement => (
      <>
        {allProductTypes
          ?.filter(
            t =>
              (!courseId && !allCourses?.filter(c => c.productTypes.find(pt => pt.id === t.id)).length)
              || allCourses?.filter(c => c.id === courseId).some(c => c.productTypes.find(pt => pt.id === t.id)),
          )
          .map(i => {
            const products = creatorProducts?.products.filter(h => h.productTypes.find(t => t.id === i.id));
            const buttons = document.getElementById(i.id)?.querySelectorAll('button') || [];
            const completedButtons = Array.from(buttons).filter(button => button.title === 'Mark as not completed');
            const completedPercents = format.percentageComplete({ completed: completedButtons.length, total: buttons.length });
            const howManyPurchased = purchasedHandbooks?.filter(p => p.productTypes.find(t => t.id === i.id))?.length || 0;
            if (products?.length === 0) return null;
            return (
              <details className={s.product} id={i.id} key={i.id}>
                <summary>
                  <div>
                    {i.thumbnail?.url && <Image image={i.thumbnail.url} alt="" />}
                    <div>
                      <h2>{i.title}</h2>
                      {products?.length && (
                        <small>
                          {products.length} byte{(products.length > 1 && 's') || ''}{' '}
                          {(howManyPurchased > 0 && (
                            <>
                              · <b>{`${howManyPurchased === products.length ? 'All' : howManyPurchased} available`}</b>
                            </>
                          ))
                            || ''}
                        </small>
                      )}
                      {completedPercents > 0 && <progress value={completedPercents} max="100" />}
                    </div>
                  </div>
                  <CaretCircleDown weight="fill" />
                </summary>
                <ul>
                  {products?.map(h => {
                    const isPurchased = purchasedHandbooks?.find(p => p.id === h.id);
                    const stepsProgress = purchasedHandbooks?.filter(p => p.id === h.id)?.[0]?.countHandbookSteps || 0;
                    return (
                      <li key={h.id}>
                        {(clickedByte === h.id && <ProgressIndicator size={20} />) || <MarkCompleted isDisabled={!isPurchased} id={h.id} />}
                        <CustomLink
                          style={(clickedByte === h.id && { opacity: 0.5 }) || {}}
                          to={getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug: h.slug })}
                          onClick={() => setClickedByte(h.id)}
                          className={!isPurchased ? s.notPurchasedHandbook : undefined}
                        >
                          {h.title} {isPurchased && stepsProgress > 1 && <small>·&nbsp;{stepsProgress} steps</small>}
                        </CustomLink>
                      </li>
                    );
                  })}
                </ul>
              </details>
            );
          })}
      </>
    );

    return (
      <div className={s.library}>
        {allProductTypes?.length === 0 && <p className="body-sm">You have no items to view in your library.</p>}
        {allCourses?.map(c => (
          <div className={s.course} key={c.id}>
            <div>
              {c.title && <h2 className="body-md-bold">{c.title}</h2>}
              <Spacer size={4} />
              {c.description && <div className="caption" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(c.description.html) }} />}
            </div>
            {listProductTypes(c.id)}
          </div>
        ))}
        {listProductTypes()}
      </div>
    );
  };

  return (
    <>
      <NoodleIsNowGladeBanner />
      {showSidebar && (
        <m.nav initial={{ x: -280 }} animate={{ x: 0 }} className={s.sidebar}>
          <div className={s.sidebarCreator}>
            <div>
              <CustomLink
                to={getUrl(IDENTIFIERS.CREATOR, {
                  creatorSlug,
                })}
              >
                {creatorData?.logoForBranding
                  ? (
                    <Image isSquare image={creatorData?.logoForBranding.url} alt={creatorData?.name || ''} className={s.sidebarLogo} />
                  )
                  : (
                    <div className={s.sidebarCreatorName}>
                      <UserImage name={creatorData?.name} size={32} url={creatorData?.person?.image?.url} color="var(--color-primary)" />
                      <p>{creatorData?.name}</p>
                    </div>
                  )}
              </CustomLink>
            </div>
          </div>
          {organization && (
            <>
              <div className={s.org}>
                <p className="caption">
                  <span>Your Organization</span>
                  <Buttons isWrapper onClick={() => setTeamModalOpen(true)}>Manage</Buttons>
                </p>
                <h2 className="body-sm-bold">{organization.name}</h2>
              </div>
              {teamModalOpen && <ManageTeamModal organizationId={organization.id} onClose={() => setTeamModalOpen(false)} />}
            </>
          )}
          {!organization && !isFetchingOrganization && customer?.canCreateOrganization && (
            <div className={s.createOrganizationButton}>
              <Buttons isSecondary isFullWidth onClick={() => setCreateOrganizationModalOpen(true)}>
                Create Organization
              </Buttons>
            </div>
          )}
          <ul>
            {conversationSid && !showChatInProfile && <li>
              <CustomLink
                to={getUrl(IDENTIFIERS.CONVERSATION_SID, {
                  conversationSid,
                })}
                className={s.sidebarChatButton}
              >
                <ChatsCircle
                  size={16}
                  color={'var(--color-gray-100)'}
                  weight={'regular'}
                />{' '}
                Chat
              </CustomLink>
            </li>}
            {tabsToShow
              .filter(tab => tab.id !== 'chat' || showChatInProfile)
              .map(tab => (
                <li key={tab.id}>
                  <button onClick={() => handleTabChange(tab.id)} className={tab.id === currentTab ? s.active : ''}>
                    <tab.icon
                      size={16}
                      color={tab.id === currentTab ? 'var(--color-primary)' : 'var(--color-gray-100)'}
                      weight={tab.id === currentTab ? 'fill' : 'regular'}
                    />{' '}
                    {tab.label}
                  </button>
                </li>
              ))}
          </ul>
          <div className={s.sidebarUser}>
            <CustomLink to={getUrl(IDENTIFIERS.USER_PROFILE_EDIT, { creatorSlug })}>
              <UserImage color={userProfile?.primaryColour?.hex} url={userProfile?.image?.url || ''} name={userProfile?.name || ''} size={32} />
              <div>
                <p>{userProfile?.name}</p>
                <small>Account Details</small>
              </div>
            </CustomLink>
            <AssociatedUserSelect redirectTo={getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug })} />
          </div>
        </m.nav>
      )}
      <div className={currentTab === 'chat' ? s.wrapperChat : s.wrapper} style={userWorkflowId ? { maxWidth: 'initial', padding: isMobile ? 0 : '0 24px' } : {}}>
        {!hideMobileNavigation && <>
          {!showSidebar && (
            <>
              {organization && (
                <>
                  <div className={s.orgMobile}>
                    <p className="caption">
                      {organization.name} <Buttons isWrapper onClick={() => setTeamModalOpen(true)}>Manage organization</Buttons>
                    </p>
                  </div>
                  {teamModalOpen && <ManageTeamModal organizationId={organization.id} onClose={() => setTeamModalOpen(false)} />}
                </>
              )}
              <nav className={s.nav}>
                <BackButton
                  title=""
                  backLink={
                    userWorkflowId
                      ? getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug, tab: 'activity' })
                      : getUrl(isInPopoverWidget() ? IDENTIFIERS.CREATOR_POPOVER_WIDGET_HOME : IDENTIFIERS.CREATOR, { creatorSlug })
                  }
                />
                <div className={s.userInfo}>
                  <UserImage color={userProfile?.primaryColour?.hex} url={userProfile?.image?.url || ''} name={userProfile?.name || ''} size={32} />
                  <AssociatedUserSelect />
                </div>
                <CustomLink to={getUrl(IDENTIFIERS.USER_PROFILE_EDIT, { creatorSlug })} className={s.navAccount}>
              Account Details
                </CustomLink>
              </nav>
            </>
          )}
          {creatorData?.id && creatorSlug && !!subscriptions?.length && currentTab !== 'chat' && (
            <div className={s.subscriptions}>
              <UserSubscriptions
                creatorSlug={creatorSlug}
                subscriptions={subscriptions}
                creatorId={creatorData?.id}
                stripeAccountId={creatorData?.stripe_account_id || ''}
                activeType={activeType}
                setActiveType={setActiveType}
                retryPayment={retryPayment}
              />
            </div>
          )}</>}
        {isCreateCommunityPostModalOpen && (
          <CreateEditBroadcastModal
            setIsVisible={setCreateCommunityPostModalOpen}
            onPublish={handlePublishPost}
            creatorName={broadcast?.creator.name}
            creatorSlug={creatorSlug}
            productSlug={broadcast?.slug}
            isCommunityPost={true}
            broadcastTiers={[]}
          />
        )}
        {!showSidebar && !hideMobileNavigation && <RadioInputCards inputName="tabs" inputs={tabsToShow} className={s.tabs} onChange={handleTabChange} />}
        <main className={currentTab === 'chat' ? s.mainChat : s.main}>
          {currentTab === 'chat' && (
            <div>
              <NewConversationWithCreator isEmbedded creatorSlug={creatorSlug} isOnUserProfile={showSidebar} />
            </div>
          )}
          {currentTab === 'library' && (
            <>
              <h1>Library</h1>
              {getLibrary()}
            </>
          )}
          {currentTab === 'bookmarks' && (
            <>
              <h1>Bookmarks</h1>
              <YourBookmarks creatorSlug={creatorSlug} />
            </>
          )}
          {currentTab === 'services' && documentRequests && !hasUserWorkflows && (
            <>
              <h1>Services</h1>
              <UserDocumentRequests requests={documentRequests.items} />
            </>
          )}
          {currentTab === 'activity' && hasUserWorkflows && creatorData && (
            <>
              {userWorkflowId
                ? (
                  <>
                    <BackButton />
                    <UserWorkflowDetailCustomerView userWorkflowId={userWorkflowId} />
                  </>
                )
                : (
                  <>
                    <h1>Activity</h1>
                    <UserWorkflows specifiedCreatorId={creatorData.id} specifiedCreatorSlug={creatorData.slug} personId={userId} isUserView />
                  </>
                )}
            </>
          )}
          {currentTab === 'community' && (
            <>
              <div className={s.header}>
                <h1>Community</h1>
              </div>
              {subscriptions
                && subscriptions.find(
                  sub => sub.subscriptionStatus === SubscriptionStatus.Active || sub.subscriptionStatus === SubscriptionStatus.Trialing,
                ) && <StartPostButton onClick={() => setCreateCommunityPostModalOpen(true)} />}
              {creatorName && (
                <div className={s.chips}>
                  <FilterChip
                    label={`${creatorName}'s broadcasts only`}
                    onClick={() => setBroadcastsOnly(!broadcastsOnly)}
                    isSelected={broadcastsOnly}
                    Icon={FunnelSimple}
                  />
                  {!!broadcastTags.length && (
                    <Chips
                      data={[{ id: 'View All', label: 'View All' }, ...broadcastTags.map(tag => ({ id: tag, label: tag }))]}
                      selectedItem={selectedChip}
                      onItemClick={e => setSelectedChip(e)}
                    />
                  )}
                </div>
              )}
              {broadcastData && !!broadcastData.length && (
                <BroadcastContainer
                  key={'Broadcasts'}
                  allBroadcastLink={getUrl(IDENTIFIERS.CREATOR, { creatorSlug, tab: 'Broadcasts' })}
                  howMany={isInPopoverWidget() ? 2 : 6}
                  creatorSlug={creatorSlug}
                  isList
                  shouldNavigate={false}
                  data={broadcastData
                    .filter(comment => comment.parent === null)
                    .filter(comment => (broadcastsOnly ? comment.owner?.name === creatorName : true))
                    .map(comment => ({
                      createdAt: comment.createdAt,
                      creatorSlug,
                      enableLink: true,
                      id: comment.id,
                      inContainer: true,
                      isNew: differenceInDays(new Date(), new Date(comment.createdAt)) < 2,
                      media: comment.medias?.[0],
                      messageId: comment.id,
                      numComments: comment.children.length,
                      participants: formatParticipants(comment),
                      productSlug: broadcast?.slug || null,
                      text: comment.text,
                      title: comment.title || 'Untitled Broadcast',
                    }))}
                />
              )}
              {comments && comments.items.length > 0 && creatorName && creatorPersonId && creatorSlug && (
                <CreatorActivity creatorName={creatorName} creatorId={creatorPersonId} creatorSlug={creatorSlug} data={comments?.items || []} />
              )}
            </>
          )}
          {currentTab === 'bookings' && upcomingBookings && (upcomingBookings?.length || 0) > 0 && creatorTimezone && (
            <>
              {unscheduledBookingsByPrice.map(bookings => (
                <UnscheduledBookings
                  key={bookings[0].id}
                  bookings={bookings}
                  onSchedule={async () => {
                    await getUpcomingBookings({ creatorSlug });
                  }}
                />
              ))}
              {(scheduledBookings?.length || 0) > 0 && (
                <>
                  <h2 className="heading-sm">Upcoming Bookings</h2>
                  <div className={s.bookings}>
                    {scheduledBookings
                      ?.filter(b => b.startAt && b.endAt)
                      .map(b => (
                        <>
                          <BookingCard
                            key={b.id}
                            booking={b}
                            creatorTimezone={creatorTimezone}
                            productType={NoodleProductTypes.InPersonSession}
                            productTitle={b.product?.title || ''}
                            priceTitle={b.price?.priceTitle || ''}
                            isRescheduleAvailable={!b.startAt || new Date(b.startAt) > new Date(new Date().setHours(new Date().getHours() + 48))}
                            onFinish={async () => {
                              getUpcomingBookings({ creatorSlug });
                            }}
                          />
                        </>
                      ))}
                  </div>
                </>
              )}
            </>
          )}
          {currentTab === null && (
            <>
              <div className={s.noActivityIcon}>
                <Pulse size={32} />
              </div>
              <h1 className='heading-sm'>No activity has taken place yet.</h1>
              <p className='body-sm'>Take a look at <CustomLink to={getUrl(IDENTIFIERS.CREATOR, { creatorSlug })}>{creatorName}&apos;s products</CustomLink>, or <CustomLink to={getUrl(IDENTIFIERS.CONVERSATION_SID, {
                conversationSid: conversationSid || '',
              })}>start a chat.</CustomLink></p>
            </>
          )}
        </main>
      </div>
      {isCreateOrganizationModalOpen && creatorData?.id && (
        <CreateOrganizationModal
          creatorId={creatorData.id}
          beforeClose={async () => { await getOrganization({ creatorId: creatorData.id }); }}
          onClose={() => setCreateOrganizationModalOpen(false)}
        />
      )}
    </>
  );
};

export default UserProfile;
